import { kebabCase } from "@/Utils/formatters";
import { Flex, Select, Text, TooltipWrapper } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import {
  MAPPING_TEST_PREFIX,
  ValidMappedFieldCheck
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/Utils";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { CustomMappingPopover } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/CustomMappingPopover";
import {
  SYSTEM_CALCULATE,
  SYSTEM_GENERATED_CAPABLE_FIELDS,
  kPositionBudgetedSalary
} from "@coreapp/constants";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import * as React from "react";

export const FieldMappingRow = ({
  fieldKey,
  fieldValue,
  localConfig,
  BuildFieldMappingOptions,
  handleFieldRename,
  handleCustomFieldNameValidation,
  disabled = false,
  required = false,
  tooltipProps = undefined,
  infoMessage = undefined,
  onUpdate = undefined
}) => {
  const posConfig = new PositionConfig(localConfig);
  const isSystemGenerationCapable = SYSTEM_GENERATED_CAPABLE_FIELDS.includes(fieldKey);
  const isSystem = fieldValue === SYSTEM_CALCULATE;

  // Check if this field can use system generation
  const canSystemCalculate = () => {
    if (fieldKey === kPositionBudgetedSalary) {
      return posConfig.canSystemGeneratePositionBudgetedSalary();
    }
    return isSystemGenerationCapable;
  };

  const getFieldOptions = () => {
    const standardOptions = BuildFieldMappingOptions(fieldKey);

    if (!isSystemGenerationCapable || !onUpdate) {
      return standardOptions;
    }

    const systemOption = {
      label: appstr("System calculate for me"),
      value: "system",
      disabled: disabled || !canSystemCalculate(),
      clickHandler: () => handleFieldMappingChange("system")
    };

    // Add tooltip when system calculate is disabled due to dependencies
    if (fieldKey === kPositionBudgetedSalary && !canSystemCalculate()) {
      systemOption.content = (
        <TooltipWrapper
          tooltipProps={{
            message: appstr(
              "System calculation requires both Staffing target and Cost band maximum to be mapped"
            ),
            position: "top"
          }}
        >
          <Text size={"small"}>{systemOption.label}</Text>
        </TooltipWrapper>
      );
    }

    return [systemOption, ...standardOptions];
  };

  const handleFieldMappingChange = value => {
    if (!onUpdate) return;

    // Convert "system" to SYSTEM_CALCULATE for storage
    const actualValue = value === "system" ? SYSTEM_CALCULATE : value;

    onUpdate(
      {
        fields: { ...localConfig.fields, [fieldKey]: actualValue }
      },
      ["fields"]
    );
  };

  const getSelectedLabel = () => {
    if (isSystem) {
      return appstr("System calculate for me");
    }
    return fieldValue ?? appstr("Select field");
  };

  return (
    <Flex direction={"column"} gap={"16px"}>
      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        key={`${fieldValue}-${fieldKey}`}
      >
        <Flex direction={"column"} gap={"6px"} width={"50%"}>
          <Select
            dataTestId={`${MAPPING_TEST_PREFIX}-field-select-${kebabCase(fieldKey)}`}
            inputOnly={false}
            label={appstr(fieldKey)}
            labelInfo={infoMessage}
            message={ValidMappedFieldCheck(localConfig, { [fieldKey]: fieldValue }, fieldKey)}
            placeholder={getSelectedLabel()}
            selectSize={"medium"}
            optionSize={"medium"}
            listWidth="fill"
            listProps={FieldSelectListProps()}
            value={isSystem ? "system" : fieldValue}
            options={getFieldOptions()}
            disabled={disabled}
            require={required}
            tooltipProps={tooltipProps}
          />
        </Flex>
        <CustomMappingPopover
          onUpdate={handleFieldRename}
          fieldID={fieldKey}
          currentField={localConfig["fields"][fieldKey]}
          validateFxn={handleCustomFieldNameValidation}
          disabled={disabled || isSystem}
        />
      </Flex>
    </Flex>
  );
};
